import React, { useState, useEffect } from 'react';
import { supabase } from "../integrations/supabase/client";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { useToast } from "../hooks/use-toast";
import { 
  Search, 
  TrendingUp, 
  Users, 
  Phone, 
  MapPin, 
  Home,
  Loader2,
  Eye,
  UserCheck
} from "lucide-react";

interface ExternalMarketAnalysis {
  id: string;
  property_id: string;
  total_found: number;
  potential_leads: number;
  analysis_date: string;
  market_insights: {
    summary: string;
    price_comparison: string;
    captation_opportunities: number;
    market_range: {
      min: number;
      max: number;
      avg: number;
    };
    contact_breakdown: {
      owners: number;
      brokers: number;
      unknown: number;
    };
    recommendations: string[];
  };
  scraped_properties: Array<{
    title: string;
    price: number;
    location?: string;
    address?: string;
    neighborhood?: string;
    city?: string;
    bedrooms?: number;
    bathrooms?: number;
    area?: number;
    description?: string;
    contact?: {
      type: 'owner' | 'broker' | 'unknown';
      name?: string;
      phone?: string;
      whatsapp?: string;
    };
    portal: string;
    url?: string;
    link?: string;
    phone?: string;
    pricePerSqm?: number;
  }>;
}

interface ExternalMarketAnalysisProps {
  propertyId: string;
  autoAnalyze?: boolean;
}

export const ExternalMarketAnalysis: React.FC<ExternalMarketAnalysisProps> = ({ 
  propertyId, 
  autoAnalyze = false 
}) => {
  const [externalAnalysis, setExternalAnalysis] = useState<ExternalMarketAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { toast } = useToast();

  const fetchExternalAnalysis = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('external_market_analysis')
        .select('*')
        .eq('property_id', propertyId)
        .maybeSingle();

      if (error) {
        console.error('Erro ao buscar análise externa:', error);
        return;
      }

      if (data) {
        setExternalAnalysis({
          ...data,
          market_insights: data.market_insights as any,
          scraped_properties: data.scraped_properties as any
        });
      }
    } catch (error) {
      console.error('Erro ao buscar análise externa:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const runExternalAnalysis = async () => {
    try {
      setIsAnalyzing(true);
      
      console.log('🔍 Invocando scraping de portais brasileiros para property:', propertyId);
      
      // Buscar dados da propriedade primeiro para enviar parâmetros completos
      const { data: propertyData, error: propertyError } = await supabase
        .from('properties')
        .select('*')
        .eq('id', propertyId)
        .single();

      if (propertyError) {
        console.error('Erro ao buscar dados da propriedade:', propertyError);
        throw propertyError;
      }

      const { data, error } = await supabase.functions.invoke('scrape-brazilian-portals', {
        body: { 
          property_id: propertyId,
          property_type: propertyData.property_type,
          neighborhood: propertyData.neighborhood,
          city: propertyData.city,
          price: propertyData.list_price,
          bedrooms: propertyData.bedrooms,
          bathrooms: propertyData.bathrooms,
          area: propertyData.area_built || propertyData.area_total,
        }
      });
      
      console.log('✅ Resposta do scraping:', { data, error });

      if (error) {
        console.error('❌ Erro no scraping:', error);
        toast({
          title: "Erro na análise",
          description: "Erro ao analisar portais externos. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      if (data && data.success) {
        toast({
          title: "Análise concluída!",
          description: `Encontrados ${data.total_found} imóveis similares. ${data.properties.filter((p: any) => p.phone).length} contatos identificados.`,
        });
      } else {
        toast({
          title: "Análise com limitações",
          description: "Alguns dados podem estar simulados devido a limitações de scraping.",
          variant: "default",
        });
      }

      // Recarregar dados
      await fetchExternalAnalysis();
    } catch (error) {
      console.error('❌ Erro na análise externa:', error);
      toast({
        title: "Erro na análise",
        description: "Erro ao analisar o mercado externo.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  useEffect(() => {
    fetchExternalAnalysis();
    if (autoAnalyze && !externalAnalysis) {
      runExternalAnalysis();
    }
  }, [propertyId]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const getContactTypeIcon = (type: string) => {
    switch (type) {
      case 'owner': return <UserCheck className="h-4 w-4 text-green-600" />;
      case 'broker': return <Users className="h-4 w-4 text-blue-600" />;
      default: return <Users className="h-4 w-4 text-gray-400" />;
    }
  };

  const getContactTypeBadge = (type: string) => {
    switch (type) {
      case 'owner': return <Badge variant="default" className="bg-green-100 text-green-800">Proprietário</Badge>;
      case 'broker': return <Badge variant="secondary">Corretor</Badge>;
      default: return <Badge variant="outline">Não identificado</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span>Carregando análise externa...</span>
        </div>
      </Card>
    );
  }

  if (!externalAnalysis) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Análise de Mercado Externa</h3>
          <p className="text-gray-600 mb-4">
            Analise imóveis similares em portais como OLX e ZAP para encontrar oportunidades de captação.
          </p>
          <Button 
            onClick={runExternalAnalysis}
            disabled={isAnalyzing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isAnalyzing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Analisando portais...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Analisar Mercado Externo
              </>
            )}
          </Button>
        </div>
      </Card>
    );
  }

  const insights = externalAnalysis.market_insights;

  return (
    <div className="space-y-6">
      {/* Header com resumo */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Análise de Mercado Externa
          </h3>
          <Button 
            onClick={runExternalAnalysis}
            disabled={isAnalyzing}
            variant="outline"
            size="sm"
          >
            {isAnalyzing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Atualizar
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <Eye className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-blue-600">{externalAnalysis.total_found}</div>
            <div className="text-sm text-gray-600">Imóveis Encontrados</div>
          </div>
          
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <UserCheck className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-green-600">{externalAnalysis.potential_leads}</div>
            <div className="text-sm text-gray-600">Oportunidades de Captação</div>
          </div>
          
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <TrendingUp className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
            <div className="text-2xl font-bold text-yellow-600">
              {insights.market_range ? formatCurrency(insights.market_range.avg) : 'N/A'}
            </div>
            <div className="text-sm text-gray-600">Preço Médio</div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-gray-700">{insights.summary}</p>
          <p className="text-gray-700">{insights.price_comparison}</p>
        </div>
      </Card>

      {/* Detalhes da análise */}
      {insights.market_range && (
        <Card className="p-6">
          <h4 className="text-md font-semibold mb-4 flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" />
            Análise de Preços
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-lg font-bold text-red-600">{formatCurrency(insights.market_range.min)}</div>
              <div className="text-sm text-gray-600">Menor Preço</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{formatCurrency(insights.market_range.avg)}</div>
              <div className="text-sm text-gray-600">Preço Médio</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">{formatCurrency(insights.market_range.max)}</div>
              <div className="text-sm text-gray-600">Maior Preço</div>
            </div>
          </div>

          {insights.contact_breakdown && (
            <div className="mt-4">
              <h5 className="font-medium mb-2">Distribuição de Contatos:</h5>
              <div className="flex gap-4 text-sm">
                <span className="flex items-center">
                  <UserCheck className="h-4 w-4 mr-1 text-green-600" />
                  {insights.contact_breakdown.owners} Proprietários
                </span>
                <span className="flex items-center">
                  <Users className="h-4 w-4 mr-1 text-blue-600" />
                  {insights.contact_breakdown.brokers} Corretores
                </span>
                <span className="flex items-center">
                  <Users className="h-4 w-4 mr-1 text-gray-400" />
                  {insights.contact_breakdown.unknown} Não identificados
                </span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Recomendações */}
      {insights.recommendations && insights.recommendations.length > 0 && (
        <Card className="p-6">
          <h4 className="text-md font-semibold mb-3">Recomendações</h4>
          <ul className="space-y-2">
            {insights.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start">
                <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span className="text-gray-700">{rec}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Lista de imóveis encontrados */}
      {externalAnalysis.scraped_properties && externalAnalysis.scraped_properties.length > 0 && (
        <Card className="p-6">
          <h4 className="text-md font-semibold mb-4 flex items-center">
            <Home className="h-4 w-4 mr-2" />
            Imóveis Similares Encontrados
          </h4>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {externalAnalysis.scraped_properties.map((property, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <h5 className="font-medium text-gray-900 line-clamp-2 flex-1 mr-2">{property.title}</h5>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{property.portal}</Badge>
                    {(property.url || property.link) && (property.url !== `Portal ${property.portal}` && property.link !== `Portal ${property.portal}`) && (
                      <a 
                        href={property.url || property.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Ver Anúncio →
                      </a>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <div className="text-lg font-bold text-green-600 mb-1">
                      {formatCurrency(property.price)}
                      {property.pricePerSqm && (
                        <span className="text-sm text-gray-500 ml-2">
                          R$ {property.pricePerSqm}/m²
                        </span>
                      )}
                    </div>
                    <div className="flex items-center text-sm text-gray-600 mb-1">
                      <MapPin className="h-3 w-3 mr-1" />
                      {property.location || property.address || `${property.neighborhood}, ${property.city}`}
                    </div>
                    <div className="flex gap-3 text-sm text-gray-600">
                      {property.bedrooms && (
                        <span>{property.bedrooms} quartos</span>
                      )}
                      {property.bathrooms && (
                        <span>{property.bathrooms} banheiros</span>
                      )}
                      {property.area && (
                        <span>{property.area}m²</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    {property.contact && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-end">
                          {getContactTypeIcon(property.contact.type)}
                          <span className="ml-2">{getContactTypeBadge(property.contact.type)}</span>
                        </div>
                        
                        {(property.contact?.phone || property.phone) && (
                          <div className="flex items-center justify-end text-sm text-gray-600">
                            <Phone className="h-3 w-3 mr-1" />
                            <span className="font-mono">{property.contact?.phone || property.phone}</span>
                          </div>
                        )}
                        
                        {property.contact?.whatsapp && (
                          <a 
                            href={`https://wa.me/55${property.contact.whatsapp.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-green-600 hover:text-green-800 text-sm"
                          >
                            <span className="mr-1">📱</span>
                            WhatsApp
                          </a>
                        )}
                        
                        {/* Se tem telefone mas não tem WhatsApp definido, oferecer link WhatsApp */}
                        {(property.phone || property.contact?.phone) && !property.contact?.whatsapp && (
                          <a 
                            href={`https://wa.me/55${(property.phone || property.contact?.phone || '').replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-green-600 hover:text-green-800 text-sm"
                          >
                            <span className="mr-1">📱</span>
                            WhatsApp
                          </a>
                        )}
                        
                        {property.contact.type === 'owner' && (
                          <div className="mt-2">
                            <Badge variant="default" className="bg-green-100 text-green-800 text-xs">
                              🎯 Oportunidade de Captação
                            </Badge>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {property.description && property.description !== property.title && (
                  <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-700">
                    {property.description.substring(0, 150)}
                    {property.description.length > 150 && '...'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="text-xs text-gray-500 text-center">
        Última análise: {new Date(externalAnalysis.analysis_date).toLocaleString('pt-BR')}
      </div>
    </div>
  );
};